/**
 * Backlog 192 phase 2 / decision 027a — caller-model address utilities.
 *
 * Addresses appear in cloudgrid.yaml under `calls:` and `callers.allowed_apps:`.
 * The platform identifies entities by entity_id at the grant lifecycle layer,
 * but the user-facing yaml refers to them by stable address strings:
 *
 *   org:<org-slug>/<entity-slug>          a specific entity in a specific org
 *   space:<space-slug>/<entity-slug>      a specific entity in a space
 *   space:<space-slug>/*                  any entity in a space (allowed_apps only)
 *
 * Two pieces live here:
 *
 *   - `parseCallerAddress(addr)` — validate the syntax and split into parts.
 *   - `callerTokenEnvVar(addr)`  — derive the K8s Secret env-var name the
 *     deployer mounts the per-pair SA token into.
 *
 * Decision 027a §"SA token contract" §"Storage" pins the env-var transform:
 *
 *   - uppercase every character
 *   - `-` → `_`
 *   - `/` → `_2_`
 *   - `:` → `_`
 *
 * Worked example from the contract:
 *
 *   org:atomic/billing-service
 *     → ORG:ATOMIC/BILLING-SERVICE
 *     → CLOUDGRID_CALLER_TOKEN_ORG_ATOMIC_2_BILLING_SERVICE
 *
 * Wildcards in `space:<slug>/*` are NOT encoded — wildcards never become an
 * env var, since the deployer never mints a per-pair token for a wildcard
 * (each matched entity gets its own concrete grant + env var).
 */

const ORG_ADDRESS_RE = /^org:([a-z0-9-]+)\/([a-z0-9-]+)$/;
const SPACE_ADDRESS_RE = /^space:([a-z0-9-]+)\/([a-z0-9-]+|\*)$/;

export type CallerAddressScope = 'org' | 'space';

export interface CallerAddress {
  scope: CallerAddressScope;
  /** Org slug for `org:` addresses; space slug for `space:` addresses. */
  ownerSlug: string;
  /** Entity slug, or `'*'` for `space:<slug>/*` wildcards. */
  entitySlug: string;
  /** True iff `entitySlug === '*'`. Convenience for callers. */
  isWildcard: boolean;
  /** The original raw address string the parse came from. */
  raw: string;
}

/**
 * Parse a caller-model address. Returns `null` when the input does not match
 * the supported syntaxes — callers decide what error to surface.
 *
 * Accepts:
 *   org:<slug>/<entity>     — both `org` and `space` allowed_apps and `calls:` entries
 *   space:<slug>/<entity>   — `allowed_apps` only (Phase 2 deployer enforces)
 *   space:<slug>/*          — `allowed_apps` wildcard, only valid for `allowed_apps`
 *
 * The slug-shape is the standard DNS-1123 / decision 010 form: lowercase
 * letters, digits, hyphens. Length bounds are not enforced here (the entity
 * lookup at deploy time will reject any slug the system never produced).
 */
export function parseCallerAddress(addr: string): CallerAddress | null {
  if (typeof addr !== 'string') return null;

  const orgMatch = ORG_ADDRESS_RE.exec(addr);
  if (orgMatch) {
    return {
      scope: 'org',
      ownerSlug: orgMatch[1],
      entitySlug: orgMatch[2],
      isWildcard: false,
      raw: addr,
    };
  }

  const spaceMatch = SPACE_ADDRESS_RE.exec(addr);
  if (spaceMatch) {
    const entitySlug = spaceMatch[2];
    return {
      scope: 'space',
      ownerSlug: spaceMatch[1],
      entitySlug,
      isWildcard: entitySlug === '*',
      raw: addr,
    };
  }

  return null;
}

/**
 * Encode a caller-model address into the K8s Secret env-var name the deployer
 * uses to mount the per-pair SA token. Per decision 027a §"SA token contract"
 * §"Storage". Throws on wildcard addresses (no token is minted for a
 * wildcard).
 */
export function callerTokenEnvVar(addr: string): string {
  const parsed = parseCallerAddress(addr);
  if (!parsed) {
    throw new Error(
      `callerTokenEnvVar: not a valid caller-model address: ${JSON.stringify(addr)}`,
    );
  }
  if (parsed.isWildcard) {
    throw new Error(
      `callerTokenEnvVar: wildcard addresses do not have a single token env var: ${JSON.stringify(addr)}`,
    );
  }
  const encoded = addr
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\//g, '_2_')
    .replace(/:/g, '_');
  return `CLOUDGRID_CALLER_TOKEN_${encoded}`;
}
